import { Injectable } from '@angular/core';
import {Observable, of} from 'rxjs';
import { BlobDto, BreadCrumbsDto, CoursesDto, ElementDto, IomDto, IomScoreDto, DefaultService as OepApi, ProfileDto, StepDto, StepSideEnum, VariantDto } from '@rsmu/oep-api';
import {catchError, map, tap} from "rxjs/operators";
import {NavigationDto} from "@rsmu/oep-api/model/navigationDto";
import {BiszVariantDto} from "@rsmu/oep-api/model/biszVariantDto";

@Injectable({
  providedIn: 'root'
})
export class OepApiService {

  private fileCache = new Map<string, BlobDto>();
  private iconCache = new Map<string, BlobDto>();

  constructor(
    private readonly oepApi: OepApi

  ) {}

  getCertificate$(iomId: string, certificateId: string) {
    return this.oepApi.downloadCertificate(iomId, certificateId);
  }

  viewCertificate(iomId: string, certificateId: string) {
    return this.oepApi.viewCertificate(iomId, certificateId, 'body', false, {
      httpHeaderAccept: 'application/pdf'
    })
  }

  getStepById$ (iomId:string, elementId: string): Observable<ElementDto | null> {
    return this.oepApi.getElementInIomByIds(iomId, elementId);
  }

  getModule$ (iomId: string): Observable<IomDto> {
    return this.oepApi.getIomMenuByIomId(iomId);
  }

  getTitle$ (iomId: string): Observable<IomDto> {
    return this.oepApi.getIomTitle(iomId);
  }

  getBreadcrumbs$(iomId: string, elementId: string): Observable<BreadCrumbsDto> {
    return this.oepApi.getBreadCrumbs(iomId, elementId)
      .pipe(
        map(breadcrumbsDto => {
          const breadCrumbsArray = Array.isArray(breadcrumbsDto.breadCrumbs)
            ? breadcrumbsDto.breadCrumbs
            : [];
          return {
            headerName: breadcrumbsDto.headerName  || '',
            breadCrumbs: breadCrumbsArray,
          };
        }),
        catchError(err => {
          return of({
            headerName: '',
            breadCrumbs: []
          })
        })
      )
  }

  getBtzVariantProtocol(variantId: string) {
    return this.oepApi.getBtzVariantProtocol(variantId);
  }

  getButton$(iomId: string): Observable<StepDto>;
  getButton$ (iomId: string, elementId: string, side: StepSideEnum): Observable<StepDto>
  getButton$ (iomId: string, elementId?: string, side?: StepSideEnum ) {
    if(elementId && side) {
      return this.oepApi.getStepByElementInIom(iomId, elementId, side);
    } else {
      return this.oepApi.getContinueBtn(iomId);
    }
  }

  generateQuizVariant(iomId: string, blockId: string): Observable <VariantDto> {
    return this.oepApi.generateNewBtzVariant(iomId, blockId);
  }

  generateBiszVariant(iomId: string, blockId: string): Observable<BiszVariantDto | null> {
    return this.oepApi.generateNewBiszVariant(iomId, blockId);
  }

  markBlockViewed (iomId: string, blockId: string): Observable<void> {
    return this.oepApi.markBlockViewed(iomId, blockId);
  }

  downloadFile (iomId: string, blockId: string, fileId: string): Observable<BlobDto> {

    if (this.fileCache.has(fileId)) {
      return of(this.fileCache.get(fileId)!)
    }

    return this.oepApi.downloadFile(iomId, blockId, fileId)
    .pipe(
      tap(blob => this.fileCache.set(fileId, blob))
    );

  }

  loadIcon (iconId: string, fileId: string): Observable<BlobDto> {

    if (this.iconCache.has(iconId)) {
      return of(this.iconCache.get(iconId)!)
    }

    return this.oepApi.loadIcon(iconId, fileId)
    .pipe(
      tap(blob => this.iconCache.set(iconId, blob))
    );
  }

  getTotalScore(iomId: string): Observable <IomScoreDto> {
    return this.oepApi.getIomScore(iomId);
  }

  getNavMenu$(iomId: string): Observable<NavigationDto> {
    return this.oepApi.getIomNavigationByIomId(iomId);
  }

  clearIom$(iomId: string): Observable<void> {
    return this.oepApi.resetIom(iomId);
  }

  getProfile$ (): Observable<ProfileDto> {
    return this.oepApi.getProfile();
  }

  getAllCourses(): Observable <CoursesDto> {
    return this.oepApi.getAllCourses();
  }
}
